import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, AtSign, Tag } from 'lucide-react';
import { CATEGORY_META } from './mailUtils';

const AB = `${process.env.REACT_APP_BACKEND_URL}/api/address-book`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// split on , or ; — but not inside <…> or inside "quoted names" (e.g. "Lee, Calvin" <calvin@…>)
export const parseAddrs = (s) => (s || '').split(/[,;](?![^<]*>)(?=(?:[^"]*"[^"]*")*[^"]*$)/).map(x => x.trim()).filter(Boolean).map(x => {
  const m = x.match(/^"?([^"<]*)"?\s*<([^>]+)>$/);
  return m ? { name: m[1].trim(), email: m[2].trim().toLowerCase() } : { name: '', email: x.replace(/[<>]/g, '').toLowerCase() };
});
const quoteName = (n) => (/[,;"<>]/.test(n) ? `"${n.replace(/"/g, '')}"` : n);
export const joinAddrs = (list) => list.map(a => (a.name ? `${quoteName(a.name)} <${a.email}>` : a.email)).join(', ');

/** To / Cc / Bcc field: chips + 365 address book autocomplete grouped by category; "@domain" lists everyone at a company. */
export const RecipientInput = ({ value, onChange, placeholder, testid, autoFocus, onCategorised }) => {
  const chips = parseAddrs(value);
  const [text, setText] = useState('');
  const [groups, setGroups] = useState([]);
  const [flat, setFlat] = useState([]);
  const [domainMode, setDomainMode] = useState(false);
  const [hi, setHi] = useState(0);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    const q = text.trim();
    if (q.length < 1) { setGroups([]); setFlat([]); setOpen(false); return undefined; }
    timer.current = setTimeout(() => {
      axios.get(`${AB}/suggest`, { ...auth(), params: { q, limit: 12 } }).then(r => {
        const have = new Set(chips.map(c => c.email));
        const g = r.data.groups.map(x => ({ ...x, contacts: x.contacts.filter(c => !have.has(c.email)) })).filter(x => x.contacts.length);
        setGroups(g); setFlat(g.flatMap(x => x.contacts)); setDomainMode(!!r.data.domain_mode); setHi(0); setOpen(true);
      }).catch(() => {});
    }, 140);
    return () => clearTimeout(timer.current);
  }, [text]);

  const commit = (list) => { onChange(joinAddrs(list)); };
  const add = (c) => { if (!c?.email) return; commit([...chips, { name: c.name || '', email: c.email }]); setText(''); setOpen(false); inputRef.current?.focus(); };
  const addRaw = () => {
    const raw = text.trim().replace(/[,;]$/, '');
    if (!raw) return;
    if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(raw)) add({ email: raw });
  };
  const addAllDomain = () => { commit([...chips, ...flat.map(c => ({ name: c.name || '', email: c.email }))]); setText(''); setOpen(false); };
  const remove = (i) => commit(chips.filter((_, j) => j !== i));

  const onKey = (e) => {
    if (e.key === 'Backspace' && !text && chips.length) { remove(chips.length - 1); return; }
    if (!open) { if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') { if (text.trim()) { e.preventDefault(); addRaw(); } } return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); flat[hi] ? add(flat[hi]) : addRaw(); }
    else if (e.key === ',' || e.key === ';') { e.preventDefault(); addRaw(); }
    else if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
  };

  const categorise = async (e, c, cat) => {
    e.stopPropagation(); e.preventDefault();
    try {
      await axios.post(`${AB}/categorise`, { email: c.email, name: c.name, category: cat }, auth());
      setGroups(gs => gs.map(g => ({ ...g, contacts: g.contacts.map(x => (x.email === c.email ? { ...x, category: cat } : x)) })));
      onCategorised?.(c, cat);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 px-1 py-1.5 focus-within:border-sky-500 min-h-[38px]" onClick={() => inputRef.current?.focus()}>
        {chips.map((c, i) => (
          <span key={c.email + i} className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5 text-slate-800 max-w-full" title={c.email} data-testid={`${testid}-chip-${i}`}>
            <span className="truncate">{c.name || c.email}</span>
            <button type="button" onClick={e => { e.stopPropagation(); remove(i); }} className="p-0.5 rounded-full text-slate-400 hover:text-red-600 hover:bg-white"><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input ref={inputRef} value={text} onChange={e => setText(e.target.value)} onKeyDown={onKey} onBlur={() => setTimeout(() => { setOpen(false); addRaw(); }, 160)} onFocus={() => text && flat.length && setOpen(true)}
          placeholder={chips.length ? '' : placeholder} autoFocus={autoFocus} className="flex-1 min-w-[140px] bg-transparent text-sm text-slate-900 placeholder:text-slate-400 outline-none py-0.5" data-testid={`${testid}-input`} autoComplete="off" />
      </div>
      {open && groups.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-xl border border-slate-200 bg-white shadow-2xl max-h-80 overflow-y-auto" data-testid={`${testid}-suggestions`} onMouseDown={e => e.preventDefault()}>
          {domainMode && flat.length > 1 && (
            <button type="button" onClick={addAllDomain} className="w-full text-left px-3 py-2 text-xs font-semibold text-sky-800 bg-sky-50 hover:bg-sky-100 flex items-center gap-1.5 border-b border-sky-100" data-testid={`${testid}-add-domain`}><AtSign className="w-3.5 h-3.5" />Add all {flat.length} people at this domain</button>
          )}
          {groups.map(g => (
            <div key={g.category}>
              <div className="px-3 pt-2 pb-1 flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${CATEGORY_META[g.category]?.cls}`}>{g.label}</span>
                <span className="text-[10px] text-slate-400">{g.contacts.length}</span>
              </div>
              {g.contacts.map(c => { const idx = flat.indexOf(c); return (
                <div key={c.email} role="option" aria-selected={idx === hi} onClick={() => add(c)} onMouseEnter={() => setHi(idx)}
                  className={`group px-3 py-1.5 flex items-center gap-2 cursor-pointer ${idx === hi ? 'bg-sky-50' : 'hover:bg-slate-50'}`} data-testid={`${testid}-opt-${c.email}`}>
                  <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-700 text-[11px] font-black flex items-center justify-center shrink-0">{(c.name || c.email).slice(0, 1).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 truncate">{c.name || c.email.split('@')[0]}{c.title ? <span className="text-slate-500 text-xs"> · {c.title}</span> : null}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.email}{c.company ? ` · ${c.company}` : ''}</div>
                  </div>
                  {c.category === 'uncategorised' && (
                    <select onClick={e => e.stopPropagation()} onChange={e => categorise(e, c, e.target.value)} defaultValue="" className="text-[10px] border border-slate-200 rounded px-1 py-0.5 text-slate-600 bg-white opacity-0 group-hover:opacity-100 focus:opacity-100" title="Set category" data-testid={`${testid}-cat-${c.email}`}>
                      <option value="" disabled>Set type…</option>
                      {['customer', 'vendor', 'subcontractor', 'engineer', 'other'].map(k => <option key={k} value={k}>{CATEGORY_META[k].label}</option>)}
                    </select>
                  )}
                  {c.category !== 'uncategorised' && <Tag className="w-3 h-3 text-slate-300 shrink-0" />}
                </div>); })}
            </div>
          ))}
          <div className="px-3 py-1.5 text-[10px] text-slate-400 border-t border-slate-100">↑↓ to move · Enter to add · type <b>@domain</b> to see everyone at a company</div>
        </div>
      )}
    </div>
  );
};
